import { Input, Modal, Select, InputNumber } from 'antd';
import { Button as AntButton } from 'antd';
import React, { useContext, useEffect, useState } from 'react';
import StateMachineContext from '@/contexts/StateMachineContext';
import AceEditor from 'react-ace';
import {
  MinusCircleOutlined,
  PlusCircleFilled,
  ExpandOutlined,
  CompressOutlined,
} from '@ant-design/icons';
import { Button } from '@hydrodigital/design-system';

interface propertyTypes {
  selectedNode: any;
  onCancel: Function;
}

export default function ActionModal({
  selectedNode,
  onCancel,
}: propertyTypes): React.JSX.Element {
  const { value: context, setValue: setContext } =
    useContext(StateMachineContext);
  const [action, setAction] = useState<any>({
    name: null,
    type: null,
    input: null,
    output: null,
    constant: null,
    value: null,
    statement: null,
    tableRows: [],
  });
  const [newRowInput, setNewRowInput] = useState<number | null>(null);
  const [newRowOutput, setNewRowOutput] = useState<number | null>(null);
  const [statements, setStatements] = useState<Array<{constant: string, value: string}>>([]);
  const [newConstant, setNewConstant] = useState<string | null>(null);
  const [newValue, setNewValue] = useState<any>(null);
  const [isEditorExpanded, setIsEditorExpanded] = useState<boolean>(false);
  const [setpointSearch, setSetpointSearch] = useState<string>('');

  const onDeleteRow = (index: number) => {
    const updatedRows = action.tableRows.filter(
      (_: any, rowIndex: number) => rowIndex !== index,
    );
    setAction({ ...action, tableRows: updatedRows });
  };

  const onDeleteStatement = (index: number) => {
    const updatedStatements = statements.filter((_, i) => i !== index);
    setStatements(updatedStatements);
    
    // Actualizar el statement principal si hay statements
    if (updatedStatements.length > 0) {
      const combinedStatement = updatedStatements
        .map(s => `${s.constant}=${s.value}`)
        .join(';');
      setAction({ ...action, statement: combinedStatement });
    } else {
      setAction({ ...action, statement: null });
    }
  };

  // Función para obtener las constantes ya utilizadas
  const getUsedConstants = () => {
    return statements.map(statement => statement.constant);
  };

  // Función para filtrar las opciones disponibles excluyendo las ya utilizadas
  const getAvailableConstants = () => {
    const usedConstants = getUsedConstants();
    return context.outputsList
      .filter(output => !usedConstants.includes(output.label))
      .map(output => ({
        label: output.label,
        value: output.label,
      }));
  };

  const filterOptions = (
    input: string,
    option?: { label: string; value: string },
  ) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

  useEffect(() => {
    setAction({
      ...selectedNode.data.value,
      tableRows: selectedNode.data.value?.tableRows ?? [],
    });
    
    // Inicializar statements si existen en el nodo seleccionado
    if (selectedNode.data.value?.statements) {
      setStatements(selectedNode.data.value.statements);
    } else if (selectedNode.data.value?.constant && selectedNode.data.value?.value) {
      // Si no hay statements pero hay una constante y valor, inicializar con esos valores
      setStatements([{
        constant: selectedNode.data.value.constant,
        value: selectedNode.data.value.value
      }]);
    } else {
      setStatements([]);
    }
  }, [selectedNode, context]);

  return (
    <Modal
      title="Link Action"
      open={true}
      footer={[]}
      onCancel={() => {
        onCancel();
      }}
    >
      <div className="flex column">
        <Input
          type="text"
          placeholder="Action name"
          onChange={(event) => {
            setAction({ ...action, name: event.target.value });
          }}
          className="mb-1"
          value={action.name}
        />
        <Select
          style={{ width: '100%' }}
          placeholder="Select an option"
          onChange={(val) => {
            setAction({ ...action, type: val });
          }}
          options={[
            { value: 'FUNCTION', label: 'Function' },
            { value: 'CONSTANT', label: 'Assignment' },
            { value: 'PID', label: 'PID Controller' },
            { value: 'TABLE', label: 'Table' },
          ]}
          value={action.type}
          className="mb-1"
        />
        {action.type == 'FUNCTION' && (
          <>
            <div className="flex mb-1">
              <span style={{ width: '11%' }}>Input</span>
              <Select
                mode="multiple"
                style={{ width: '89%' }}
                showSearch
                placeholder="Search"
                optionFilterProp="children"
                onChange={(val) => {
                  setAction({ ...action, input: val });
                }}
                onSearch={() => {}}
                filterOption={filterOptions}
                options={context.inputsList.map((input: any) => {
                  return {
                    label: input.label,
                    value: input.label,
                  };
                })}
                value={action.input}
              />
            </div>
            <div className="flex mb-1">
              <span style={{ width: '11%' }}>Output</span>
              <Select
                mode="multiple"
                style={{ width: '89%' }}
                showSearch
                placeholder="Search"
                optionFilterProp="children"
                onChange={(val) => {
                  setAction({ ...action, output: val });
                }}
                onSearch={() => {}}
                filterOption={filterOptions}
                options={[
                  ...context.outputsList.map((output: any) => ({
                    label: output.label,
                    value: output.label,
                  })),
                  ...context.constantsList.map((constant: any) => ({
                    label: `Constants.${constant.name}`,
                    value: `Constants.${constant.name}`,
                  })),
                ]}
                value={action.output}
                tagRender={(props) => {
                  const { label, closable, onClose } = props;
                  const index = (action.output || []).indexOf(props.value);
                  return (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        background: '#f0f0f0',
                        borderRadius: '4px',
                        padding: '2px 8px',
                        margin: '2px',
                        fontSize: '12px',
                      }}
                    >
                      <span style={{ color: '#078CB3', fontWeight: 'bold', marginRight: '4px' }}>
                        {index + 1}:
                      </span>
                      {label}
                      {closable && (
                        <span
                          onClick={onClose}
                          style={{ marginLeft: '4px', cursor: 'pointer', fontSize: '10px' }}
                        >
                          ×
                        </span>
                      )}
                    </span>
                  );
                }}
              />
            </div>
            <div className="flex mb-1" style={{ position: 'relative' }}>
              <AceEditor
                className="mt-1"
                mode="python"
                theme="github"
                name="editor"
                onChange={(val) => {
                  setAction({ ...action, statement: val });
                }}
                value={action.statement}
                fontSize={14}
                showPrintMargin={true}
                showGutter={true}
                highlightActiveLine={true}
                setOptions={{
                  useWorker: false,
                  showLineNumbers: true,
                  tabSize: 2,
                }}
                placeholder="Type your code here"
                style={{ maxHeight: isEditorExpanded ? '500px' : '120px', width: '100%' }}
              />
              <AntButton
                size="small"
                icon={isEditorExpanded ? <CompressOutlined /> : <ExpandOutlined />}
                onClick={() => setIsEditorExpanded(!isEditorExpanded)}
                style={{
                  position: 'absolute',
                  right: '4px',
                  bottom: '4px',
                  zIndex: 10,
                  opacity: 0.8,
                }}
                title={isEditorExpanded ? 'Collapse editor' : 'Expand editor'}
              />
            </div>
          </>
        )}
        {action.type == 'CONSTANT' && (
           <>
            {/* Mostrar statements existentes */}
            {statements.map((statement, index) => (
              <div key={index} className="flex mb-1">
                <Select
                  value={statement.constant}
                  showSearch
                  placeholder="Search"
                  optionFilterProp="children"
                  onChange={(val) => {
                    const updatedStatements = [...statements];
                    updatedStatements[index].constant = val;
                    setStatements(updatedStatements);
                    
                    // Actualizar el statement principal
                    const combinedStatement = updatedStatements
                      .map(s => `${s.constant}=${s.value}`)
                      .join(';');
                    setAction({ ...action, statement: combinedStatement, statements: updatedStatements });
                  }}
                  filterOption={filterOptions}
                  options={context.outputsList
                    .filter(output => 
                      output.label === statement.constant || 
                      !statements.some(s => s.constant === output.label)
                    )
                    .map(output => ({
                      label: output.label,
                      value: output.label,
                    }))}
                  style={{ width: '45%' }}
                />
                <span className="mx-2">=</span>
                <Input
                  value={statement.value}
                  placeholder="Value"
                  onChange={(event) => {
                    const updatedStatements = [...statements];
                    updatedStatements[index].value = event.target.value;
                    setStatements(updatedStatements);
                    
                    // Actualizar el statement principal
                    const combinedStatement = updatedStatements
                      .map(s => `${s.constant}=${s.value}`)
                      .join(';');
                    setAction({ ...action, statement: combinedStatement, statements: updatedStatements });
                  }}
                  style={{ width: '45%' }}
                />
                <AntButton
                  className="ml-2"
                  style={{ border: 'none', color: 'red' }}
                  icon={<MinusCircleOutlined />}
                  onClick={() => onDeleteStatement(index)}
                />
              </div>
            ))}
            
            {/* Fila para añadir nuevo statement */}
            <div className="flex mb-1">
              <Select
                value={newConstant}
                showSearch
                placeholder="Search"
                optionFilterProp="children"
                onChange={setNewConstant}
                filterOption={filterOptions}
                options={getAvailableConstants()}
                style={{ width: '45%' }}
              />
              <span className="mx-2">=</span>
              <Input
                value={newValue}
                placeholder="Value"
                onChange={(e) => setNewValue(e.target.value)}
                style={{ width: '45%' }}
              />
              <AntButton
                className="ml-2"
                style={{ border: 'none' }}
                icon={<PlusCircleFilled />}
                onClick={() => {
                  if (newConstant && newValue) {
                    const newStatementObj = { constant: newConstant, value: newValue };
                    const updatedStatements = [...statements, newStatementObj];
                    setStatements(updatedStatements);
                    
                    // Actualizar el statement principal
                    const combinedStatement = updatedStatements
                      .map(s => `${s.constant}=${s.value}`)
                      .join(';');
                    setAction({
                      ...action,
                      statement: combinedStatement,
                      statements: updatedStatements
                    });
                    
                    // Limpiar los campos
                    setNewConstant(null);
                    setNewValue(null);
                  }
                }}
              />
            </div>
          </>
        )}
        {action.type == 'PID' && (
          <>
            {/* Input: Measurements + Constants */}
            <div className="flex mb-1" style={{ alignItems: 'center' }}>
              <span style={{ width: '80px', minWidth: '80px' }}>Input</span>
              <Select
                style={{ width: '100%' }}
                showSearch
                placeholder="Search"
                optionFilterProp="children"
                onChange={(val) => {
                  setAction({ ...action, input: val });
                }}
                filterOption={filterOptions}
                options={[
                  ...context.inputsList.map((input: any) => ({
                    label: input.label,
                    value: input.label,
                  })),
                  ...context.constantsList.map((constant: any) => ({
                    label: `Constants.${constant.name}`,
                    value: `Constants.${constant.name}`,
                  })),
                ]}
                value={action.input}
              />
            </div>

            {/* Setpoint: float, Measurement, or Constants */}
            <div className="flex mb-1" style={{ alignItems: 'center' }}>
              <span style={{ width: '80px', minWidth: '80px' }}>Setpoint</span>
              <Select
                style={{ width: '100%' }}
                showSearch
                placeholder="Search or type a number"
                optionFilterProp="children"
                onChange={(val) => {
                  setAction({ ...action, setpoint: val });
                }}
                filterOption={(input, option) => {
                  if (!option) return false;
                  return (option.label ?? '').toLowerCase().includes(input.toLowerCase());
                }}
                onSearch={(val) => {
                  setSetpointSearch(val);
                }}
                options={[
                  ...(setpointSearch && !isNaN(Number(setpointSearch)) ? [{
                    label: setpointSearch,
                    value: setpointSearch,
                  }] : []),
                  ...context.inputsList.map((input: any) => ({
                    label: input.label,
                    value: input.label,
                  })),
                  ...context.constantsList.map((constant: any) => ({
                    label: `Constants.${constant.name}`,
                    value: `Constants.${constant.name}`,
                  })),
                ]}
                value={action.setpoint}
              />
            </div>

            <div className="flex mb-1" style={{ alignItems: 'center' }}>
              <span style={{ width: '80px', minWidth: '80px' }}>Output</span>
              <Select
                style={{ width: '100%' }}
                showSearch
                placeholder="Search"
                optionFilterProp="children"
                onChange={(val) => {
                  setAction({ ...action, output: val });
                }}
                filterOption={filterOptions}
                options={context.outputsList.map((output: any) => ({
                  label: output.label,
                  value: output.label,
                }))}
                value={action.output}
              />
            </div>

            {/* PID Controller Parameters */}
            <div className="flex mb-1" style={{ alignItems: 'center' }}>
              <span style={{ width: '80px', minWidth: '80px' }}>Kp</span>
              <InputNumber
                style={{ width: '100%' }}
                value={action.kp}
                placeholder="Kp"
                onChange={(val) => setAction({ ...action, kp: val })}
              />
            </div>
            <div className="flex mb-1" style={{ alignItems: 'center' }}>
              <span style={{ width: '80px', minWidth: '80px' }}>Ki</span>
              <InputNumber
                style={{ width: '100%' }}
                value={action.ki}
                placeholder="Ki"
                onChange={(val) => setAction({ ...action, ki: val })}
              />
            </div>
            <div className="flex mb-1" style={{ alignItems: 'center' }}>
              <span style={{ width: '80px', minWidth: '80px' }}>Kd</span>
              <InputNumber
                style={{ width: '100%' }}
                value={action.kd}
                placeholder="Kd"
                onChange={(val) => setAction({ ...action, kd: val })}
              />
            </div>
            <div className="flex mb-1" style={{ alignItems: 'center' }}>
              <span style={{ width: '80px', minWidth: '80px' }}>Min</span>
              <InputNumber
                style={{ width: '100%' }}
                value={action.min}
                placeholder="Min"
                onChange={(val) => setAction({ ...action, min: val })}
              />
            </div>
            <div className="flex" style={{ alignItems: 'center' }}>
              <span style={{ width: '80px', minWidth: '80px' }}>Max</span>
              <InputNumber
                style={{ width: '100%' }}
                value={action.max}
                placeholder="Max"
                onChange={(val) => setAction({ ...action, max: val })}
              />
            </div>
          </>
        )}
        {action.type == 'TABLE' && (
          <>
            {/* Input and Output selectors */}
            <div className="flex mb-1">
              <span style={{ width: '11%' }}>Input</span>
              <Select
                style={{ width: '89%' }}
                showSearch
                placeholder="Search Input"
                optionFilterProp="children"
                onChange={(val) => setAction({ ...action, tableInput: val })}
                filterOption={filterOptions}
                options={(context.inputsList || []).map((input) => ({
                  label: input.label,
                  value: input.label,
                }))}
                value={action.tableInput}
              />
            </div>

            <div className="flex mb-1">
              <span style={{ width: '11%' }}>Output</span>
              <Select
                style={{ width: '89%' }}
                showSearch
                placeholder="Search Output"
                optionFilterProp="children"
                onChange={(val) => setAction({ ...action, tableOutput: val })}
                filterOption={filterOptions}
                options={(context.outputsList || []).map((output) => ({
                  label: output.label,
                  value: output.label,
                }))}
                value={action.tableOutput}
              />
            </div>

            <div className="mt-2">
              <div className="flex mb-1">
                <div style={{ width: '45%', marginRight: '2px' }}>
                  <span style={{ width: '60%' }}>Input</span>
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="Input"
                    value={newRowInput}
                    onChange={setNewRowInput}
                  />
                </div>
                <div style={{ width: '45%' }}>
                  <span style={{ width: '60%' }}>Output</span>
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="Output"
                    value={newRowOutput}
                    onChange={setNewRowOutput}
                  />
                </div>
                <div style={{ width: '10%' }}>
                  <AntButton
                    style={{ width: '10%', border: 'none' }}
                    icon={<PlusCircleFilled />}
                    onClick={() => {
                      if (newRowInput !== null && newRowOutput !== null) {
                        const updatedRows = [
                          ...action.tableRows,
                          { input: newRowInput, output: newRowOutput },
                        ];
                        setAction({ ...action, tableRows: updatedRows });
                        setNewRowInput(null);
                        setNewRowOutput(null);
                      }
                    }}
                    className=" mt-3 ml-2"
                  />
                </div>
              </div>
              {action.tableRows?.map((row: any, index: any) => (
                <div key={index} className="flex ">
                  <div style={{ width: '45%', marginRight: '2px' }}>
                    <InputNumber
                      style={{ width: '100%' }}
                      value={row.input}
                      readOnly
                    />
                  </div>
                  <div style={{ width: '45%' }}>
                    <InputNumber
                      style={{ width: '100%' }}
                      value={row.output}
                      readOnly
                    />
                  </div>
                  <div style={{ width: '10%' }}>
                    <AntButton
                      className=" ml-2"
                      style={{ width: '10%', border: 'none', color: 'red' }}
                      icon={<MinusCircleOutlined />}
                      onClick={() => onDeleteRow(index)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        <div className="flex justify-center pt-2">
          <Button
            label="Save"
            variant="primary"
            showIcon={false}
            size="small"
            onClick={() => {
              const actionToSave = {
                ...action,
                statements: statements
              };
              selectedNode.data.onChange(actionToSave, selectedNode.id);
              onCancel();
            }}
          />
        </div>
      </div>
    </Modal>
  );
}
